import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios';
import { useTheme } from '../contexts/ThemeContext';
import { useToastContext } from '../contexts/ToastContext';
import { sanitizeInput } from '../config/security';

export default function Notifications() {
  const { isDark } = useTheme();
  const toast = useToastContext();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications');
      setNotifications(response.data?.data || response.data || []);
    } catch (err) {
      setError('Gagal memuat notifikasi');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000); // Refresh setiap 5 detik
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      if (!notificationId || (typeof notificationId !== 'number' && typeof notificationId !== 'string')) {
        throw new Error('ID notifikasi tidak valid');
      }

      const sanitizedId = encodeURIComponent(String(notificationId));
      // Validate endpoint to prevent SSRF
      const endpoint = `/notifications/${sanitizedId}`;
      if (!endpoint.startsWith('/notifications/')) {
        throw new Error('Invalid endpoint');
      }
      await api.put(endpoint, { is_read: true });

      setNotifications(notifications.map(notification =>
        notification.id === notificationId
          ? { ...notification, is_read: true }
          : notification
      ));
    } catch (err) {
      console.error('Error marking notification as read:', err);
      toast.error('Gagal menandai notifikasi sebagai dibaca');
    }
  };

  const deleteNotification = async (notificationId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus notifikasi ini?')) return;

    try {
      if (!notificationId || (typeof notificationId !== 'number' && typeof notificationId !== 'string')) {
        throw new Error('ID notifikasi tidak valid');
      }

      const sanitizedId = encodeURIComponent(String(notificationId));
      // Validate endpoint to prevent SSRF
      const endpoint = `/notifications/${sanitizedId}`;
      if (!endpoint.startsWith('/notifications/')) {
        throw new Error('Invalid endpoint');
      }
      await api.delete(endpoint);

      setNotifications(notifications.filter(notification => notification.id !== notificationId));
      toast.success('Notifikasi berhasil dihapus');
    } catch (err) {
      console.error('Error deleting notification:', err);
      toast.error('Gagal menghapus notifikasi');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      info: '📢',
      success: '✅',
      warning: '⚠️',
      error: '❌'
    };
    return icons[type] || '📢';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Baru saja';
    if (minutes < 60) return `${minutes} menit lalu`;
    if (hours < 24) return `${hours} jam lalu`;
    return `${days} hari lalu`;
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-96 ${isDark ? 'bg-gray-900' : ''}`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Memuat notifikasi...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-8 text-center`}
      >
        <div className="text-6xl mb-4 text-red-500">⚠️</div>
        <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
          Kesalahan Memuat Notifikasi
        </h2>
        <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{error}</p>
        <button
          onClick={fetchNotifications}
          className={`px-6 py-2 rounded-lg transition-colors ${isDark ? 'bg-gray-600 text-gray-100 hover:bg-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
        >
          Coba Lagi
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6`}
      >
        <h1 className={`text-3xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}>
          Notifikasi
        </h1>
        <p className={`mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Kelola dan lihat semua notifikasi sistem
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4"
      >
        {notifications.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-12 text-center`}
          >
            <div className={`text-6xl mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              🔔
            </div>
            <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              Tidak Ada Notifikasi
            </h3>
            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
              Belum ada notifikasi yang tersedia
            </p>
          </motion.div>
        ) : (
          notifications.slice().reverse().map((notification, index) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 ${!notification.is_read ? 'ring-2 ring-blue-500/20' : ''
                }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <span className="text-2xl">{getNotificationIcon(notification.type)}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'} ${!notification.is_read ? 'font-bold' : ''}`}>
                      {sanitizeInput(notification.title)}
                    </h3>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'} mt-1`}>
                      {sanitizeInput(notification.message)}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-2`}>
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  {!notification.is_read && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
                    >
                      Tandai Dibaca
                    </button>
                  )}
                  <button
                    onClick={() => deleteNotification(notification.id)}
                    className="px-3 py-1 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 transition-colors"
                  >
                    Hapus
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </motion.div>
    </motion.div>
  );
}